<template>
    <ui-card class="pd-settings-card">
        <div class="columns">
            <div class="column is-narrow">
                <i class="fad fa-briefcase fa-2x has-text-primary"></i>
            </div>
            <div class="column">
        <div class="form-head">
            <div class="has-text-weight-bold is-size-5">Your Details</div>
            <div class="is-size-7 has-text-grey">Your district, degree and current column place you on the salary schedule; your renewal date sets the window your hours count in.</div>
        </div>

        <div class="columns mt-3">
            <div class="column">
                <b-field label="District" label-position="inside">
                    <b-select v-model="district_id" expanded :loading="!districts.length">
                        <option :value="0">Not Listed/Not Applicable</option>
                        <option :value="999999">Private School</option>
                        <option v-for="district in districts" v-if="district.id !== 9999999" :key="district.id" :value="district.id">{{ district.name }} ({{ district.city | capitalize }})</option>
                    </b-select>
                </b-field>
            </div>
            <div class="column">
                <b-field label="School" label-position="inside">
                    <b-select v-model="school_id" expanded>
                        <option :value="0">Not Listed/Not Applicable</option>
                        <option v-for="school in schools" :key="school.id" :value="school.id">{{ school.name }} ({{ school.city | capitalize }})</option>
                    </b-select>
                </b-field>
            </div>
        </div>

        <div class="columns is-multiline">
            <div class="column is-4">
                <b-field label="Years teaching" label-position="inside">
                    <b-select v-model="meta.years_teaching" placeholder="Select..." expanded>
                        <option v-for="option in yearsOptions" :key="option" :value="option">{{ option }}</option>
                    </b-select>
                </b-field>
            </div>
            <div class="column is-4">
                <b-field label="Highest degree" label-position="inside">
                    <b-select v-model="meta.highest_degree" placeholder="Select..." expanded>
                        <option v-for="option in degreeOptions" :key="option" :value="option">{{ option }}</option>
                    </b-select>
                </b-field>
            </div>
            <div class="column is-4">
                <b-field label="Clock hour column" label-position="inside">
                    <b-select v-model="meta.hours_column" placeholder="Select..." expanded :loading="scaleLoading">
                        <!-- The district's actual columns when a schedule is
                             on file; the standard ladder otherwise -->
                        <template v-if="scaleColumns">
                            <option v-for="option in scaleColumns" :key="'column-' + option.value" :value="option.value">{{ option.label }}</option>
                            <!-- A column on file that isn't one of the district's
                                 lanes (chosen from the generic ladder) stays
                                 selectable so the field never reads blank -->
                            <option v-if="unmatchedColumn" :value="unmatchedColumn">{{ meta.highest_degree }}{{ unmatchedColumn }} credits (on file)</option>
                            <option value="This does not apply">This does not apply</option>
                        </template>
                        <template v-else>
                            <option v-for="option in hoursOptions" :key="'column-' + option" :value="option">{{ option }}</option>
                        </template>
                    </b-select>
                </b-field>
            </div>
        </div>

        <div class="renewal-head mt-2 mb-3">
            <span class="has-text-weight-bold"><i class="fad fa-calendar-check has-text-primary mr-2"></i>Certificate Renewal</span>
        </div>
        <!-- Top labels: inside/on-border labels collide with the compact
             number-input controls -->
        <pd-renewal-fields v-model="renewal" label-position=""/>
            </div>
        </div>

        <template #footer>
            <div class="form-footer">
                <div class="is-size-7 has-text-grey">Changes apply to your salary placement, renewal tracking and the bundle builder right away.</div>
                <b-button type="is-primary" size="is-small" :loading="processing" :disabled="!loaded || !dirty" @click="save"><i class="fas fa-floppy-disk mr-2"></i>Save Changes</b-button>
            </div>
        </template>
    </ui-card>
</template>

<script>
    import {mapGetters, mapMutations} from "vuex";
    import UiCard from "../UiCard";
    import PdRenewalFields from "../fields/PdRenewalFields";
    import {client as http} from "../../http_client";
    import {YEARS_TEACHING_OPTIONS, DEGREE_OPTIONS, HOURS_COLUMN_OPTIONS, PD_META_KEYS} from "../../constants/profile";
    import {scaleColumnOptions} from "../package/scaleMath";

    /**
     * The professional-development settings, at the top of the Overview so
     * they're easy to keep current (Derek 2026-09-16): the career profile
     * (district, school, years, degree, column — what the salary placement
     * and the bundle builder read) and the certificate-renewal questions.
     * Reads through GET app/users/profile-verification (same shape the
     * modal uses) and saves through PUT app/users/pd-profile, which
     * deliberately does NOT count as a profile verification.
     */
    export default {
        name: "PdSettingsCard",
        components: {UiCard, PdRenewalFields},
        data() {
            return {
                loaded: false,
                processing: false,
                profile: {},
                meta: {years_teaching: null, highest_degree: null, hours_column: null},
                renewal: {pd_renewal_on: null, pd_external_hours: null, pd_target_hours: null},
                snapshot: null,
                districts: [],
                schools: [],
                district_id: undefined,
                school_id: undefined,
                initialSchoolId: null,
                scale: null,
                scaleLoading: false,
                yearsOptions: YEARS_TEACHING_OPTIONS,
                degreeOptions: DEGREE_OPTIONS,
                hoursOptions: HOURS_COLUMN_OPTIONS,
            };
        },
        computed: {
            ...mapGetters(['currentUser']),
            // The district's own lane names once a published schedule is on
            // file and a degree is chosen (same source as the bundle builder)
            scaleColumns() {
                return scaleColumnOptions(this.scale, this.meta.highest_degree);
            },
            unmatchedColumn() {
                const v = this.meta.hours_column;
                if (!v || v === 'This does not apply' || !this.scaleColumns) return null;
                return this.scaleColumns.some(o => o.value === v) ? null : v;
            },
            state() {
                return JSON.stringify({d: this.district_id, s: this.school_id, m: this.meta, r: this.renewal});
            },
            dirty() {
                return this.snapshot !== null && this.state !== this.snapshot;
            }
        },
        methods: {
            ...mapMutations(['setUser']),
            load() {
                http.get('app/users/profile-verification', {force: true}).then(response => {
                    this.profile = {...response.data.profile};
                    const m = response.data.meta || {};
                    this.meta = {years_teaching: m.years_teaching || null, highest_degree: m.highest_degree || null, hours_column: m.hours_column || null};
                    const renewal = {};
                    for (const key of PD_META_KEYS) renewal[key] = m[key] === undefined ? null : m[key];
                    this.renewal = renewal;
                    this.initialSchoolId = this.profile.school_id;
                    return http.get('districts');
                }).then(results => {
                    this.districts = results.data;
                    if (this.profile.district === 'Private School') {
                        this.district_id = 999999;
                    } else if (this.profile.district_id && this.districts.some(d => d.id === this.profile.district_id)) {
                        this.district_id = this.profile.district_id;
                    } else {
                        this.district_id = 0;
                    }
                    this.loaded = true;
                    // the district → school cascade settles asynchronously;
                    // snapshot after it so a restored school isn't "dirty"
                    setTimeout(() => { this.snapshot = this.state; }, 400);
                }).catch(err => {
                    console.error('Career profile load failed.', err);
                    this.$notify('error', 'Could Not Load', 'Your details could not be loaded. Please try again later.', {duration: 6000, permanent: false});
                });
            },
            loadScale(districtId) {
                this.scale = null;
                if (!districtId || districtId === 999999) {
                    this.scaleLoading = false;
                    return;
                }
                this.scaleLoading = true;
                this.$store.dispatch('getPayScale', districtId).then(scale => {
                    if (this.district_id === districtId) this.scale = scale;
                    this.scaleLoading = false;
                }).catch(() => {
                    this.scaleLoading = false;
                });
            },
            save() {
                this.processing = true;
                http.put('app/users/pd-profile', {
                    district: this.profile.district,
                    district_id: (this.district_id === 0 || this.district_id === 999999) ? null : this.district_id,
                    school: this.profile.school,
                    school_id: (this.school_id === 0) ? null : this.school_id,
                    meta: {...this.meta, ...this.renewal}
                }).then(() => {
                    this.processing = false;
                    this.snapshot = this.state;
                    const updated = {...this.currentUser, district: this.profile.district, school: this.profile.school};
                    localStorage.setItem('user', JSON.stringify(updated));
                    this.setUser(updated);
                    // The placement, the dashboard hero and the bundle
                    // builder all read these
                    this.$store.dispatch('getPd').catch(() => {});
                    this.$store.dispatch('getDashboard').catch(() => {});
                    this.$emit('saved');
                    this.$notify('success', 'Details Saved', 'Your professional development details have been saved.', {duration: 5000, permanent: false});
                }).catch(err => {
                    this.processing = false;
                    const message = (err.response && err.response.data) ? err.response.data : 'Something went wrong saving your changes. Please try again.';
                    this.$notify('error', 'Could Not Save', message, {duration: 8000, permanent: false});
                });
            }
        },
        watch: {
            // Same district → school cascade as the profile-verification modal
            district_id(val) {
                this.schools = [];
                this.loadScale(val);
                if (val === 0) {
                    this.profile.district = 'Not Listed/Not Applicable';
                } else if (val === 999999) {
                    this.profile.district = 'Private School';
                } else {
                    let code;
                    this.districts.forEach(d => {
                        if (d.id === val) {
                            this.profile.district = d.name;
                            code = d.code;
                        }
                    });
                    if (code) {
                        http.get('districts/schoolsIn/' + code).then(results => {
                            this.schools = results.data;
                            if (this.initialSchoolId && this.schools.some(s => s.id === this.initialSchoolId)) {
                                this.school_id = this.initialSchoolId;
                            }
                            this.initialSchoolId = null;
                        }).catch(err => {
                            console.error(err);
                        });
                    }
                }
                if (val === 0 || val === 999999) {
                    this.school_id = 0;
                    this.initialSchoolId = null;
                } else if (!this.initialSchoolId) {
                    this.school_id = undefined;
                }
            },
            school_id(val) {
                if (val === 0) {
                    this.profile.school = 'Not Listed/Not Applicable';
                } else {
                    this.schools.forEach(s => {
                        if (s.id === val) this.profile.school = s.name;
                    });
                }
            }
        },
        mounted() {
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .form-footer {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 1rem;
        flex-wrap: wrap;
    }
</style>
